<template>
  <div class="modal-overlay">
    <div class="mmodal-content" style="max-width: 95vw">
      <div class="modal-header">
        <h5 class="modal-title">New Pharmacy Sale</h5>
        <button type="button" class="close" @click="closeModal">&times;</button>
      </div>

      <div v-if="loading">
        <loader />
      </div>

      <div v-if="!loading" class="modal-body">
        <table class="sale-table">
          <thead>
            <tr>
              <th style="width: 5%">#</th>
              <th style="width: 29%">Item Name</th>
              <th style="width: 8%">Quantity</th>
              <th style="width: 10%">Documented Price</th>
              <th style="width: 10%">Actual Price</th>
              <th style="width: 8%">Discount</th>
              <th style="width: 10%">Profit Margin</th>
              <th style="width: 10%">Total Cost</th>
              <th style="width: 7%">Action</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, index) in saleItems" :key="index">
              <td>{{ index + 1 }}</td>
              <td>
                <iteminput
                  :Store_id="store_id"
                  :Rowid="index"
                  :type="'stock'"
                  @itemoption-check="item_option"
                />
              </td>
              <td>
                <input
                  type="number"
                  v-model.number="row.quantity"
                  min="1"
                  @input="updateRowCalculations(index, 'quantity')"
                />
              </td>
              <td>
                <input
                  type="number"
                  v-model.number="row.documented_price"
                  min="0"
                  :readonly="true"
                  class="form-control"
                  tabindex="-1"
                />
              </td>
              <td>
                <input
                  type="number"
                  v-model.number="row.actual_price"
                  min="0"
                  step="0.01"
                  @input="updateRowCalculations(index, 'actual_price')"
                />
              </td>
              <td>
                <input
                  type="number"
                  v-model.number="row.discount"
                  min="0"
                  step="0.01"
                  @input="updateRowCalculations(index, 'discount')"
                />
              </td>
              <td>
                <strong>{{
                  (row.profit_margin * row.quantity).toFixed(2)
                }}</strong>
              </td>
              <td>
                <strong>{{ (row.total_cost || 0).toFixed(2) }}</strong>
              </td>
              <td>
                <button class="btn-remove" @click="removeRow(index)">✖</button>
              </td>
            </tr>
          </tbody>

          <tfoot>
            <tr class="totals-row">
              <td colspan="2"><strong>Totals</strong></td>
              <td>
                <strong>{{ totalQuantity }}</strong>
              </td>
              <td></td>
              <td></td>
              <td>
                <strong>{{ totalDiscount.toFixed(2) }}</strong>
              </td>
              <td>
                <strong>{{ totalProfit.toFixed(2) }}</strong>
              </td>
              <td>
                <strong>{{ totalCost.toFixed(2) }}</strong>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>

        <div class="actions">
          <button class="btn-add" @click="addRow">+ Add Item</button>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="saveSale">
            Save Sale
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { generalApiRequest } from "../../../api/apiIndex";
import iteminput from "../../Inventory/components/iteminput.vue";
import { JsItemName } from "../../Inventory/components/jscomponents";
import loader from "../../special_clinics/dialysis/loader.vue";

export default {
  name: "PharmacySaleModal",
  components: {
    iteminput,
    loader,
  },
  props: {
    store_id: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saleItems: [],
    };
  },
  computed: {
    totalQuantity() {
      return this.saleItems.reduce((sum, row) => sum + (row.quantity || 0), 0);
    },
    totalDiscount() {
      return this.saleItems.reduce(
        (sum, row) => sum + (row.discount || 0) * (row.quantity || 0),
        0,
      );
    },
    totalProfit() {
      return this.saleItems.reduce(
        (sum, row) => sum + (row.total_profit || 0),
        0,
      );
    },
    totalCost() {
      return this.saleItems.reduce(
        (sum, row) => sum + (row.total_cost || 0),
        0,
      );
    },
  },
  mounted() {
    this.addRow();
  },
  methods: {
    async item_option(option, rowIndex) {
      try {
        const req_body = { item_id: option.id };
        const res = await generalApiRequest(
          "inventory/get_stock_prices",
          "POST",
          req_body,
          1,
        );

        const item_name = JsItemName(
          option.brand_name,
          option.item_strength,
          option.item_name,
        );

        const documentedPrice = res.cash_charge || 0;
        const actualPrice = res.cash_charge || 0;
        const unitBuyingPrice = res.purchase_price || 0;
        const discount = 0;
        const quantity = 1;

        const profitPerUnit = actualPrice - unitBuyingPrice;
        const totalCost = quantity * actualPrice;
        const totalProfit = profitPerUnit * quantity;

        this.saleItems[rowIndex] = {
          ...this.saleItems[rowIndex],
          item_name: item_name,
          item_id: option.id,
          unit_buying_price: unitBuyingPrice,
          documented_price: documentedPrice,
          actual_price: actualPrice,
          discount: discount,
          quantity: quantity,
          profit_margin: profitPerUnit,
          total_cost: totalCost,
          total_profit: totalProfit,
        };

        this.saleItems = [...this.saleItems];
      } catch (error) {
        console.error("Error in item_option:", error);
      }
    },

    addRow() {
      const newRow = {
        item_name: "",
        item_id: "",
        quantity: 1,
        documented_price: 0,
        actual_price: 0,
        unit_buying_price: 0,
        discount: 0,
        profit_margin: 0,
        total_cost: 0,
        total_profit: 0,
      };
      this.saleItems.push(newRow);
    },

    removeRow(index) {
      this.saleItems.splice(index, 1);
    },

    updateRowCalculations(index, changedField) {
      const row = this.saleItems[index];

      if (
        !row ||
        row.documented_price === undefined ||
        row.unit_buying_price === undefined
      ) {
        return;
      }

      if (row.quantity < 1) {
        row.quantity = 1;
      }

      if (changedField === "discount") {
        const discountValue = row.discount || 0;
        row.actual_price = Math.max(0, row.documented_price - discountValue);
      } else if (changedField === "actual_price") {
        const actualPrice = row.actual_price || 0;
        row.discount = Math.max(0, row.documented_price - actualPrice);
      }

      const profitPerUnit =
        (row.actual_price || 0) - (row.unit_buying_price || 0);
      row.profit_margin = profitPerUnit;

      const quantity = row.quantity || 0;
      row.total_cost = quantity * (row.actual_price || 0);
      row.total_profit = profitPerUnit * quantity;

      row.total_cost = parseFloat(row.total_cost.toFixed(2));
      row.total_profit = parseFloat(row.total_profit.toFixed(2));
      row.discount = parseFloat((row.discount || 0).toFixed(2));
      row.actual_price = parseFloat((row.actual_price || 0).toFixed(2));

      this.$forceUpdate();
    },

    async saveSale() {
      const validItems = this.saleItems.filter(
        (item) => item.item_id && item.quantity > 0,
      );

      if (validItems.length === 0) {
        alert("Please add at least one item to the sale");
        return;
      }

      this.loading = true;

      // Keep the original structure exactly as it was
      const req_body = {
        items: this.saleItems,
        store_id: this.store_id,
      };

      try {
        const res = await generalApiRequest(
          "inventory/submit_sale",
          "POST",
          req_body,
          1,
        );

        if (res) {
          window.location.reload();
          this.closeModal();
        } else {
          alert("An Unexpected problem Occurred. Please Try again Later");
        }
      } catch (error) {
        console.error("Error saving sale:", error);
        alert("An error occurred while saving the sale");
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      this.$emit("close-make-sales-model");
    },
  },
};
</script>

<style scoped>
.sale-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  font-size: 0.9rem;
}

.sale-table th,
.sale-table td {
  border: 1px solid #ddd;
  padding: 0.4rem;
  text-align: center;
}

.sale-table input {
  width: 100%;
  padding: 0.3rem;
  text-align: center;
}

.sale-table th {
  background-color: #f4f4f4;
  font-weight: 600;
}

.totals-row {
  background: #f0f0f0;
  font-weight: bold;
}

.totals-row td {
  font-weight: bold;
}

.actions {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-start;
}

.btn-add {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-add:hover {
  background-color: #0056b3;
}

.btn-remove {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-remove:hover {
  background-color: #c0392b;
}

.modal-footer {
  border-top: 1px solid #ddd;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: opacity 0.2s;
}

.btn:hover {
  opacity: 0.9;
}

.btn-primary {
  background-color: #28a745;
  color: white;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.close {
  background: rgb(233, 22, 22);
  border: 1px solid rgb(182, 178, 178);
  font-size: 1.5rem;
  color: white;
  line-height: 1;
  cursor: pointer;
  padding: 0 0.5rem;
  border-radius: 4px;
}

.close:hover {
  background: rgb(200, 18, 18);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.mmodal-content {
  background-color: white;
  border-radius: 8px;
  max-width: 95vw;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #ddd;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-body {
  overflow-x: auto;
}

.sale-table input[readonly] {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

.sale-table td strong {
  color: #2c3e50;
}
</style>
